/**
 * Feeds the Rebuy bundle popup (widget 289411) the product a shopper just added FROM A CARD, so the
 * popup can offer the matching bundle on a collection grid or a home row the same way it already
 * does on the product page.
 *
 * WHY THIS EXISTS AT ALL — measured on 2026-09-04 against `theme dev`, not assumed:
 *
 *   On a product page the widget container carries `data-rebuy-shopify-product-ids="{{ product.id }}"`,
 *   which lands in `widget.data.shopify_product_ids` and lets Rebuy resolve the bundles for that one
 *   product (measured: `[4933357863043]` → 3 AirMini travel bundles).
 *
 *   A page-wide mount has no such product: one widget serves every card on the page, so it comes up
 *   with `shopify_product_ids: []` and `products: []` and can offer nothing.
 *
 * WHAT ACTUALLY FIRES THE POPUP: not the widget's own `popup_trigger`. That reads `"manual"` with
 * `popup_trigger_selector: 'form[action="cart/add"]'`, and that selector matches ZERO forms in this
 * theme — the real action is `/cart/add`, with a leading slash. Rebuy fires off its own cart
 * interception instead. Measured sequence of one add on the AirMini page:
 *
 *   t+0ms     POST /cart/add.js
 *   t+1153ms  cart:update (source: product-form-component)
 *   t+1192ms  rebuy:cart.add          ← Rebuy sees the add
 *   t+1584ms  rebuy.show (289411)     ← Rebuy opens the popup by itself, 392ms later
 *
 * So this module does NOT call `show()` on the happy path. Its whole job is to make sure the widget
 * already holds the right products by the time `rebuy:cart.add` lands — roughly a 1.2s window, which
 * is why priming starts at `submit` rather than at `cart:update`.
 *
 * `show()` IS called, but only as a late fallback, and never before the add has landed. An earlier
 * version of this module showed the popup as soon as products resolved — measured at ~500ms, a full
 * ~750ms BEFORE `cart:update`. That is not merely early, it is wrong: `addUpsellToCart` works by
 * removing the line the shopper just added, so a popup answered before the add lands asks Rebuy to
 * remove a line that is not in the cart yet. One run out of four ended with an EMPTY cart — the
 * machine gone and the bundle never added. The fallback therefore starts at `cart:update`, and only
 * fires if Rebuy has not shown the popup itself by then.
 *
 * The drawer choreography is NOT here — `assets/sl-bundle-upsell-close.js` owns that, including the
 * clamp that stops Rebuy's whole-line removal from eating a unit the shopper meant to keep. Both
 * modules ship together from `snippets/sl-bundle-upsell.liquid`.
 */

/** @type {string} Rebuy widget id for the bundle popup. */
const WIDGET_ID = '289411';

/** @type {string} Written by `snippets/bundle-upsell-attribute.liquid` on qualifying card roots. */
const GATE = '[data-bundle-upsell]';

/**
 * @type {string} The theme's quick-add modal. Multi-variant products add from inside it.
 *
 * It is rendered at BODY level — measured, its ancestor chain is
 * `#quick-add-modal-content → dialog → #quick-add-dialog → body` — so a form submitted from it has
 * no gated card above it and `closest('[data-bundle-upsell]')` comes back null. That silently cost
 * the offer on 87 of the 110 tagged products (79%): they went straight to the cart, no popup.
 */
const QUICK_ADD_DIALOG = '#quick-add-dialog';

/**
 * @type {string} Announces that a prime resolved and the widget now holds offers.
 *
 * `assets/sl-bundle-upsell-pricing.js` needs this because Rebuy's own `rebuy.ready` fires once per
 * page — on a card page that is at mount time, with an EMPTY product list, and it never fires
 * again. Without this event the store-discount rewrite silently never runs off the product page.
 */
const PRODUCTS_EVENT = 'sl:bundle-upsell:products';

/** @returns {any} The mounted Rebuy widget, or undefined before Rebuy has run. */
const widget = () => window.Rebuy?.widgets?.find((w) => String(w.id) === WIDGET_ID);

/**
 * @type {number} How long after the add lands to wait for Rebuy's own popup before showing it here.
 * Measured gap between `cart:update` and Rebuy's `rebuy.show`: 360-400ms on a collection page. Kept
 * comfortably under the 1500ms GRACE in assets/sl-bundle-upsell-close.js, so the popup still wins
 * the race against the cart drawer.
 */
const SHOW_FALLBACK = 800;

/** @type {number | null} The product primed for the add currently in flight. */
let primedProductId = null;

/**
 * @type {number | null} A product whose add started before Rebuy had mounted the widget.
 *
 * Rebuy reaches `rebuy.ready` anywhere from 1s to 14s after navigation, so on a card page the very
 * first add of a session — the likeliest one there is — can easily arrive with nothing to prime.
 * Dropping it there would silently cost that shopper the offer, so it is parked here and primed the
 * moment the widget turns up.
 */
let pendingProductId = null;

/** @type {ReturnType<typeof setTimeout> | undefined} */
let showTimer;

/**
 * Points the widget at one product and asks Rebuy to resolve its bundles.
 * @param {number} productId
 */
function prime(productId) {
  const instance = widget();
  if (!instance?.data) {
    pendingProductId = productId;
    return;
  }
  pendingProductId = null;

  // Already holding this product's offers (a second add of the same card): nothing to fetch.
  if (primedProductId === productId && instance.data.products?.length) return;

  primedProductId = productId;
  instance.data.shopify_product_ids = [productId];

  try {
    // Resolution only. Showing is decided later, once the add has actually landed.
    instance.getWidgetProducts(() => {
      if (!instance.data?.products?.length) return;

      // Let the pricing module rewrite the offers before Rebuy paints them.
      document.dispatchEvent(new CustomEvent(PRODUCTS_EVENT, { detail: { widget: instance } }));
    });
  } catch (error) {
    // A failed prime costs the upsell, never the add itself — the drawer still opens.
    console.error('Priming the bundle upsell widget failed:', error);
    primedProductId = null;
  }
}

/**
 * Copies the gate onto the quick-add dialog for as long as it shows that product.
 *
 * Doing it here, rather than teaching each consumer about quick-add, is what keeps the rest of the
 * feature ignorant of it: this module and assets/sl-bundle-upsell-close.js both simply call
 * `closest('[data-bundle-upsell]')` on the submitted form, and with the dialog carrying the
 * attribute that lookup resolves from inside the modal exactly as it does from a card.
 *
 * The attribute is rewritten on EVERY choose click, and removed when the card behind it is not
 * gated — a stale value would offer the previous product's bundle for the one now on screen.
 */
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.quick-add__button--choose')) return;

    const dialog = document.querySelector(QUICK_ADD_DIALOG);
    if (!dialog) return;

    const productId = event.target.closest(GATE)?.getAttribute('data-bundle-upsell');
    if (productId) dialog.setAttribute('data-bundle-upsell', productId);
    else dialog.removeAttribute('data-bundle-upsell');
  },
  { capture: true }
);

/**
 * Clears the screen for the popup: nothing of ours may sit on top of it.
 *
 * TWO THINGS CAN COVER IT, and a covered popup is a dead click on every bundle — the shopper picks
 * a bundle and nothing happens, which is precisely the failure reported from the storefront:
 *
 *   the cart drawer   Opened by us after the PREVIOUS add was answered, so it is already on screen
 *                     when a second add raises the popup again. Measured with the drawer open:
 *                     `buttonReachable: false`, the top element over a bundle button being
 *                     `.cart-drawer__content`.
 *   the quick-add modal  A `<dialog>` in the top layer covers a plain `div` whatever its z-index.
 *                     The theme closes it on `cart:update` of its own accord; this only settles the
 *                     case where the popup wins that race.
 *
 * `assets/sl-bundle-upsell-close.js` also closes the drawer on `rebuy.show`, but only when Rebuy
 * emits that event. This runs on the same event AND directly before the fallback `show()` below, so
 * the screen is cleared on the path we drive ourselves too.
 */
function clearScreenForPopup() {
  const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
  if (drawer?.querySelector('dialog[open]')) drawer.close?.();

  const dialog = /** @type {any} */ (document.querySelector(QUICK_ADD_DIALOG));
  if (dialog?.querySelector('dialog[open]')) dialog.closeDialog?.();
}

document.addEventListener('rebuy.show', (event) => {
  if (String(/** @type {any} */ (event).detail?.widget?.id) !== WIDGET_ID) return;
  clearScreenForPopup();
});

// Capture phase so the resolution starts in parallel with the add rather than after it.
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;

    const gate = form.closest(GATE);
    if (!gate) {
      primedProductId = null;
      return;
    }

    const productId = Number(gate.getAttribute('data-bundle-upsell'));
    if (Number.isFinite(productId) && productId > 0) prime(productId);
  },
  { capture: true }
);

/**
 * Rebuy turned up after an add had already started. Prime the product that add was for — but only
 * while it is still in flight. Once `cart:update` has been and gone, the cart drawer is on screen
 * and dropping a popup over it would be worse than simply missing the offer.
 */
document.addEventListener('rebuy.ready', (event) => {
  if (String(/** @type {any} */ (event).detail?.widget?.id) !== WIDGET_ID) return;
  if (pendingProductId === null) return;

  const productId = pendingProductId;
  pendingProductId = null;
  prime(productId);
});

/**
 * The add has landed, so the cart Rebuy would act on is now the real one. Give Rebuy's own trigger
 * first refusal — that is the path the product page has always used — and only step in if it does
 * not take it.
 */
document.addEventListener('cart:update', (event) => {
  const { data } = /** @type {any} */ (event).detail ?? {};
  if (data?.didError) return;
  if (data?.source !== 'product-form-component') return;

  // The add is over: a widget that mounts from here on is too late for this one.
  pendingProductId = null;
  if (primedProductId === null) return;

  const productId = primedProductId;
  clearTimeout(showTimer);
  showTimer = setTimeout(() => {
    const instance = widget();
    if (primedProductId !== productId) return;
    if (!instance?.data?.products?.length) return;
    // Already up: Rebuy got there first, which is the expected path.
    if (instance.data.visible) return;

    try {
      clearScreenForPopup();
      instance.show();
    } catch (error) {
      console.error('Showing the bundle popup after the add landed failed:', error);
    }
  }, SHOW_FALLBACK);
});

/**
 * The offer has been answered; the next add re-primes from scratch.
 *
 * Filtered by widget id, and that filter is load-bearing: this page mounts THREE Rebuy widgets —
 * 13756, 199918 (the gift-with-purchase) and this one. Without the check, either of the other two
 * emitting `rebuy.hide`/`rebuy.add` between the `submit` and the `cart:update` would clear
 * `primedProductId`, and the `cart:update` handler above would then return early and cancel the
 * fallback `show()` — an add that qualifies, with offers resolved, and no popup.
 *
 * @param {Event} event
 */
const reset = (event) => {
  if (String(/** @type {any} */ (event).detail?.widget?.id) !== WIDGET_ID) return;

  clearTimeout(showTimer);
  primedProductId = null;
  pendingProductId = null;
};
document.addEventListener('rebuy.add', reset);
document.addEventListener('rebuy.hide', reset);
